import type { SelectOption } from '@brighterly/lib-lumee-ui'
import type { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import { trackEvent } from '@shared/lib/amplitude'
import { isoValue } from '@shared/lib/amplitude'
import { toAnalyticsId } from '@shared/lib/amplitude'

import { useLandingStore } from './store'

dayjs.extend(utc)
dayjs.extend(timezone)

interface SelectLessonTimePayload {
  disciplineCode: ComputedRef<Discipline>
}

interface SelectLessonTimeReturn {
  options: Ref<SelectOption[]>
  setChangeTime: (value: string) => void
}

const getTimeOptions = (times: string[]): SelectOption[] => times.map(time => ({
  value: time,
  text: dayjs(`2025-01-01 ${time}`, `${DateFormat.DefaultDate} ${DateFormat.DefaultTime}`).format(DateFormat.AmPm),
}))

export const useSelectLessonTime = (payload: SelectLessonTimePayload): SelectLessonTimeReturn => {
  const landingStore = useLandingStore()

  const getOptions = (): SelectOption[] => {
    if (!landingStore.selectedDate) {
      return []
    }

    const times = landingStore.getAvailableTimesForDate(landingStore.selectedDate)
    const options = getTimeOptions(times)

    return options
  }

  const options = ref<SelectOption[]>(getOptions())

  const setChangeTime = (value: string): void => {
    // The landing's subject, not the active discipline (spec D8).
    trackEvent('Field_change', {
      feature: 'landing',
      name: 'lesson_time',
      value: isoValue(`${landingStore.selectedDate} ${value}:00`),
      discipline_code: toAnalyticsId(payload.disciplineCode.value),
    })

    landingStore.setSelectedTime(value)
  }

  const setRecalculateOptions = (): void => {
    options.value = getOptions()
  }

  watch(() => landingStore.selectedDate, setRecalculateOptions, { immediate: true })

  return {
    options,
    setChangeTime,
  }
}
